import React, { useState } from 'react'

import {
  TextField,
  FormControl,
  FormHelperText,
  InputAdornment,
  IconButton,
  Box,
  Typography,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'

/**
 * @typedef {object} PasswordInputProps
 * @property {string} title - El título que se muestra sobre el campo de contraseña.
 * @property {string} [value] - El valor actual del campo de contraseña.
 * @property {string} [placeholder] - Texto de marcador de posición para el campo.
 * @property {function(React.ChangeEvent): void} onChange - La función callback que se ejecuta cuando el valor cambia.
 * @property {boolean} [error] - Si es `true`, el campo se muestra con estilo de error.
 * @property {string} [errorMessage] - El mensaje de error a mostrar cuando `error` es `true`.
 * @property {string} [helpMessage] - Un mensaje de ayuda a mostrar cuando no hay error.
 * @property {object} [InputProps] - Props adicionales a pasar al componente `TextField`.
 */

/**
 * Un componente de entrada de contraseña con funcionalidad de mostrar/ocultar.
 * 
 * Proporciona un campo de texto seguro para contraseñas con un botón de icono que permite
 * al usuario alternar entre mostrar y ocultar la contraseña. Incluye soporte para mensajes
 * de error y ayuda, y aplica estilos personalizados consistentes con el diseño de la aplicación.
 * 
 * @component
 * @example
 * const MyLoginForm = () => {
 *   const [password, setPassword] = useState('');
 *   const [error, setError] = useState(false);
 * 
 *   const handleChange = (event) => {
 *     setPassword(event.target.value);
 *     setError(event.target.value.length < 8);
 *   };
 * 
 *   return (
 *     <PasswordInput
 *       title="Contraseña"
 *       placeholder="Ingrese su contraseña"
 *       value={password}
 *       onChange={handleChange}
 *       error={error}
 *       errorMessage="La contraseña debe tener al menos 8 caracteres"
 *       helpMessage="Use una combinación de letras, números y símbolos"
 *     />
 *   );
 * };
 * 
 * @param {PasswordInputProps} props - Las props del componente PasswordInput.
 */
export const PasswordInput = ({ ...props }) => {
  const [visibility, setVisibility] = useState(false)

  return (
    <FormControl sx={{ margin: '12px 0', width: '100%' }}>
      <Typography
        sx={{
          marginBottom: '5px',
          fontFamily: 'FSEmeric-Medium',
          fontSize: 16,
        }}
      >
        {props.title}
      </Typography>
      <TextField
        {...props}
        sx={{ width: '100%' }}
        type={visibility ? 'text' : 'password'}
        variant="outlined"
        value={props.value || ''}
        placeholder={props.placeholder}
        onChange={props.onChange}
        InputProps={{
          ...props.InputProps,
          sx: { height: 48, ...props.InputProps?.sx },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setVisibility(!visibility)} size="large">
                {visibility ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <FormHelperText id="my-helper-text" error={props.error}>
        {props.error ? props.errorMessage : props.helpMessage}
      </FormHelperText>
    </FormControl>
  )
}
